import React, { useEffect, useRef, useState } from 'react';
import { TextSelection } from 'prosemirror-state';
import { showHTML } from '../../helpers/editorHelpers';
import { updateClasses, hasClass, getCurrentIndent, setIndent } from '../../helpers/contentHelpers';
import { getPreference, setPreference } from '../../helpers/preferencesHelpers';
import { createSuggestionIdentity, createSuggestionMarkAttrs } from '../../helpers/suggestionHelpers';
import Icon from '../Icon';
// lightweight toolbar for editor commands
import CaretButton from '../CaretButton';
import PopoverLinks from '../Overlays/PopoverLinks';
import Popover from '../Overlays/Popover';

const selectionIntersectsSuggestionMark = (state, markName) => {
  const selection = state?.selection;
  const doc = state?.doc;
  const markType = state?.schema?.marks?.[markName];
  if (!selection || !doc || !markType) return false;

  const from = Number(selection.from || 0);
  const to = Number(selection.to || 0);
  if (to <= from) {
    let $pos;
    try {
      $pos = doc.resolve(from);
    } catch (err) {
      $pos = null;
    }
    if (!$pos) return false;
    const marks = [
      ...($pos.marks() || []),
      ...($pos.nodeBefore?.marks || []),
      ...($pos.nodeAfter?.marks || [])
    ];
    return marks.some((mark) => mark?.type === markType);
  }

  let hit = false;
  doc.nodesBetween(from, to, (node) => {
    if (!node?.isText) return;
    const marks = Array.isArray(node.marks) ? node.marks : [];
    if (marks.some((mark) => mark?.type === markType)) {
      hit = true;
    }
  });
  return hit;
};

const selectionContainsOnlyAddSuggestion = (state) => {
  const selection = state?.selection;
  const doc = state?.doc;
  const addMarkType = state?.schema?.marks?.diffSuggestionAdd;
  const removeMarkType = state?.schema?.marks?.diffSuggestionRemove;
  if (!selection || !doc || !addMarkType) return false;

  const from = Number(selection.from || 0);
  const to = Number(selection.to || 0);
  if (to <= from) return false;

  let sawText = false;
  let hasNonAddText = false;

  doc.nodesBetween(from, to, (node) => {
    if (!node?.isText) return;
    sawText = true;
    const marks = Array.isArray(node.marks) ? node.marks : [];
    const hasAdd = marks.some((mark) => mark?.type === addMarkType);
    const hasRemove = Boolean(removeMarkType) && marks.some((mark) => mark?.type === removeMarkType);
    if (!hasAdd || hasRemove) {
      hasNonAddText = true;
    }
  });

  return sawText && !hasNonAddText;
};

function TableCreatePicker({ editor, isOpen, onClose, triggerRef }) {
  const columns = 6;
  const rows = 4;
  const [hovered, setHovered] = useState({ cols: 0, rows: 0 });

  useEffect(() => {
    if (!isOpen) {
      setHovered({ cols: 0, rows: 0 });
    }
  }, [isOpen]);

  const insertTable = (cols, rowCount) => {
    try {
      if (!editor) return;
      const didInsert = editor.chain().focus().insertTable({ rows: rowCount, cols, withHeaderRow: false }).run();
      if (didInsert && editor?.state?.tr && editor?.view?.dispatch) {
        const targetWidth = 1000;
        const widthSafety = 4;
        const availableWidth = Math.max(1, targetWidth - widthSafety);
        const perColumnWidth = Math.floor(availableWidth / Math.max(1, cols));
        const { state } = editor;
        const { selection } = state;
        const $from = selection?.$from;
        let tablePos = null;

        if ($from) {
          for (let depth = $from.depth; depth > 0; depth -= 1) {
            const node = $from.node(depth);
            if (node?.type?.name === 'table') {
              tablePos = $from.before(depth);
              break;
            }
          }
        }

        if (Number.isInteger(tablePos)) {
          const tableNode = state.doc.nodeAt(tablePos);
          if (tableNode) {
            let tr = state.tr;
            tableNode.descendants((node, pos) => {
              const isCell = node?.type?.name === 'tableCell' || node?.type?.name === 'tableHeader';
              if (!isCell) return;
              const cellPos = tablePos + pos + 1;
              const current = Array.isArray(node.attrs?.colwidth) ? node.attrs.colwidth : null;
              const nextColwidth = [perColumnWidth];
              const alreadySet = current && current.length === 1 && Number(current[0]) === Number(nextColwidth[0]);
              if (!alreadySet) {
                tr = tr.setNodeMarkup(cellPos, node.type, {
                  ...node.attrs,
                  colwidth: nextColwidth
                }, node.marks);
              }
            });
            if (tr.docChanged) {
              editor.view.dispatch(tr);
            }
          }
        }
      }
      onClose?.();
    } catch (err) {
      onClose?.();
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      placement="top"
      className="tableCreatePopover"
    >
      <div className="tableCreateGrid" role="grid" aria-label="Create table grid selector">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          Array.from({ length: columns }).map((__, colIndex) => {
            const cellCols = colIndex + 1;
            const cellRows = rowIndex + 1;
            const isFilled = hovered.cols >= cellCols && hovered.rows >= cellRows;
            return (
              <button
                key={`table-cell-${rowIndex}-${colIndex}`}
                type="button"
                className="tableCreateCell"
                aria-label={`${cellCols} columns by ${cellRows} rows`}
                onMouseEnter={() => setHovered({ cols: cellCols, rows: cellRows })}
                onFocus={() => setHovered({ cols: cellCols, rows: cellRows })}
                onMouseDown={e => e.preventDefault()}
                onClick={() => insertTable(cellCols, cellRows)}
              >
                <span className={`tableCreateCellSwatch ${isFilled ? 'fill' : ''}`} aria-hidden="true" />
              </button>
            );
          })
        ))}
      </div>
      <div className="tableCreateLabel">
        {hovered.cols > 0 && hovered.rows > 0 ? `${hovered.cols} x ${hovered.rows}` : 'Insert table'}
      </div>
    </Popover>
  );
}


export default function ToolBar({
  editor,
  setViewMode,
  viewMode,
  diffMode: controlledDiffMode = null,
  setContent,
  id = 'toolbar',
  projectId = null,
  itemsMap = null,
  onOpenProjectDoc = null,
  onDiffModeChange = null
}) {
  // Safely run editor commands
  const runSafe = (fn) => {
    if (!editor) return;
    try { fn(); } catch (err) { /* swallow */ }
  };

  // force re-render on editor events so isActive checks stay current
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const upd = () => setTick(t => t + 1);
    try {
      editor.on && editor.on('selectionUpdate', upd);
      editor.on && editor.on('update', upd);
      editor.on && editor.on('focus', upd);
      editor.on && editor.on('blur', upd);
    } catch (err) {}
    return () => {
      try {
        editor.off && editor.off('selectionUpdate', upd);
        editor.off && editor.off('update', upd);
        editor.off && editor.off('focus', upd);
        editor.off && editor.off('blur', upd);
      } catch (err) {}
    };
  }, [editor]);

  const [moreActive, setMoreActive] = useState(() => {
    try {
      return !!getPreference(null, 'toolbarExpanded', false);
    } catch (err) { return false; }
  });


  const [linkMenu, setLinkMenu] = useState({ open: false, url: '', target: '', x: 0, y: 0, defaultMode: null });
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const tableMenuTriggerRef = useRef(null);
  const [diffMode, setDiffMode] = useState(() => {
    const controlled = String(controlledDiffMode || '').trim().toLowerCase();
    if (controlled === 'suggest') return 'suggest';
    if (controlled === 'write') return 'write';
    try {
      const stored = String(getPreference(null, 'editorDiffMode', 'write') || '').trim().toLowerCase();
      return stored === 'suggest' ? 'suggest' : 'write';
    } catch (err) {
      return 'write';
    }
  });

  useEffect(() => {
    const controlled = String(controlledDiffMode || '').trim().toLowerCase();
    if (controlled !== 'suggest' && controlled !== 'write') return;
    setDiffMode(prev => (prev === controlled ? prev : controlled));
  }, [controlledDiffMode]);

  const applyDiffMode = (mode) => {
    const normalized = String(mode || '').trim().toLowerCase() === 'suggest' ? 'suggest' : 'write';
    setDiffMode(normalized);
    try { setPreference(null, 'editorDiffMode', normalized); } catch (err) {}
    if (typeof onDiffModeChange === 'function') {
      try { onDiffModeChange(normalized); } catch (err) {}
    }
  };

  const runSuggestTrackedInlineCommand = (command) => {
    if (!editor || typeof command !== 'function') return;
    const state = editor.state;
    const selection = state?.selection;
    const view = editor.view;
    const dispatch = view?.dispatch;
    const addMarkType = state?.schema?.marks?.diffSuggestionAdd;
    const removeMarkType = state?.schema?.marks?.diffSuggestionRemove;
    if (!state || !selection || typeof dispatch !== 'function') return;

    if (selectionIntersectsSuggestionMark(state, 'diffSuggestionRemove')) {
      return;
    }

    if (diffMode !== 'suggest') {
      command(editor.chain().focus());
      return;
    }

    if (selection.empty || !addMarkType || !removeMarkType) {
      command(editor.chain().focus());
      return;
    }

    const from = Number(selection.from || 0);
    const to = Number(selection.to || 0);
    if (!(to > from)) {
      command(editor.chain().focus());
      return;
    }

    if (selectionContainsOnlyAddSuggestion(state)) {
      command(editor.chain().focus());
      return;
    }

    try {
      const selectedSlice = state.doc.slice(from, to);
      const identity = createSuggestionIdentity({});
      const now = Date.now();
      const sharedAttrs = {
        annotationId: identity.annotationId,
        batchId: identity.batchId,
        source: 'user_suggest',
        authorId: null,
        createdAt: now,
        status: 'pending'
      };
      const removeAttrs = createSuggestionMarkAttrs({ ...sharedAttrs, op: 'remove' });
      const addAttrs = createSuggestionMarkAttrs({ ...sharedAttrs, op: 'add' });

      let tr = state.tr;
      tr.addMark(from, to, removeMarkType.create(removeAttrs));
      tr.insert(to, selectedSlice.content);
      const addFrom = to;
      const addTo = to + selectedSlice.content.size;
      if (addTo > addFrom) {
        tr.addMark(addFrom, addTo, addMarkType.create(addAttrs));
      }
      tr.setSelection(TextSelection.create(tr.doc, addFrom, addTo));
      dispatch(tr.scrollIntoView());

      command(editor.chain().focus().setTextSelection({ from: addFrom, to: addTo }));
    } catch (err) {
      command(editor.chain().focus());
    }
  };

  // Note: keep toolbar simple; avoid experimental selection-remap code.
  const toolbarItems = [
    // Undo / Redo
    { label: 'Undo', iconClass: 'arrow-u-up-left', onClick: () => runSafe(() => editor?.chain().focus().undo().run()), isActive: () => false },
    { label: 'Redo', iconClass: 'arrow-u-up-right', onClick: () => runSafe(() => editor?.chain().focus().redo().run()), isActive: () => false },
    { label: 'Write', iconClass: 'pencil-simple-line', onClick: () => applyDiffMode('write'), isActive: () => diffMode === 'write', tooltip: 'Write mode' },
    { label: 'Suggest', iconClass: 'cursor-text', onClick: () => applyDiffMode('suggest'), isActive: () => diffMode === 'suggest', tooltip: 'Suggest mode' },
    { type: 'divider' },
    // Text formatting
    { label: 'Bold', iconClass: 'text-b', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleBold().run())), isActive: e => e?.isActive('bold') },
    { label: 'Italic', iconClass: 'text-italic', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleItalic().run())), isActive: e => e?.isActive('italic') },
    { label: 'Underline', iconClass: 'text-underline', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleUnderline().run())), isActive: e => e?.isActive('underline') },
    { label: 'Strike', iconClass: 'text-strikethrough', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleStrike().run())), isActive: e => e?.isActive('strike'), tooltip: 'Ctrl + Shift + S' },
    { type: 'divider' },

    // Headings
    { label: 'H1', iconClass: 'text-h-one', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleHeading({ level: 1 }).run())), isActive: e => e?.isActive('heading', { level: 1 }) },
    { label: 'H2', iconClass: 'text-h-two', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleHeading({ level: 2 }).run())), isActive: e => e?.isActive('heading', { level: 2 }) },
    { label: 'H3', iconClass: 'text-h-three', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleHeading({ level: 3 }).run())), isActive: e => e?.isActive('heading', { level: 3 }) },
    { label: 'H4', iconClass: 'text-h-four', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleHeading({ level: 4 }).run())), isActive: e => e?.isActive('heading', { level: 4 }) },
    { label: 'H5', iconClass: 'text-h-five', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleHeading({ level: 5 }).run())), isActive: e => e?.isActive('heading', { level: 5 }) },
    { label: 'H6', iconClass: 'text-h-six', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleHeading({ level: 6 }).run())), isActive: e => e?.isActive('heading', { level: 6 }) },

    { type: 'divider' },

    // Lists
    { label: 'Bullet List', iconClass: 'list-bullets', onClick: () => runSafe(() => editor?.chain().focus().toggleBulletList().run()), isActive: e => e?.isActive('bulletList') },
    { label: 'Ordered List', iconClass: 'list-numbers', onClick: () => runSafe(() => editor?.chain().focus().toggleOrderedList().run()), isActive: e => e?.isActive('orderedList') },
    { label: 'Task List', iconClass: 'list-checks', onClick: () => runSafe(() => editor?.chain().focus().toggleTaskList().run()), isActive: e => e?.isActive('taskList') },
    
    { type: 'divider' },

    // Link + table creation
    { label: 'Link', iconClass: 'link-simple-horizontal', onClick: () => runSafe(() => {
      try {
        if (!editor) return;

        const state = editor.state || (editor.view && editor.view.state);
        const sel = state.selection;
        let selFrom = sel.from;
        let selTo = sel.to;

        if (sel.empty) {
          const $from = sel.$from;
          const nodeStart = $from.start();
          const text = String($from.parent?.textContent || '');
          const parentOffset = Number($from.parentOffset || 0);

          if (text.length > 0) {
            let cursorIndex = Math.max(0, Math.min(parentOffset, text.length));
            const isSpaceAt = (idx) => idx < 0 || idx >= text.length || /\s/.test(text[idx]);

            if (cursorIndex > 0 && isSpaceAt(cursorIndex) && !isSpaceAt(cursorIndex - 1)) {
              cursorIndex -= 1;
            }

            if (!isSpaceAt(cursorIndex)) {
              let startOff = cursorIndex;
              let endOff = cursorIndex + 1;

              while (startOff > 0 && !isSpaceAt(startOff - 1)) startOff -= 1;
              while (endOff < text.length && !isSpaceAt(endOff)) endOff += 1;

              selFrom = nodeStart + startOff;
              selTo = nodeStart + endOff;
            }
          }

          if (selFrom !== selTo) {
            try { editor.chain().focus().setTextSelection({ from: selFrom, to: selTo }).run(); } catch (e) {}
          }
        }

        // Compute coordinates below selection
        let x = 0; let y = 0;
        try {
          if (editor.view && typeof editor.view.coordsAtPos === 'function') {
            const startCoords = editor.view.coordsAtPos(selFrom);
            const endCoords = editor.view.coordsAtPos(selTo);
            const left = Math.min(startCoords.left, endCoords.left);
            const bottom = endCoords.bottom || Math.max(startCoords.bottom, endCoords.bottom);
            x = left + window.scrollX;
            y = bottom + window.scrollY + 6;
          }
        } catch (err) {}

        // If selection already has a link, prefill href
        let currentHref = '';
        let currentTarget = '';
        try {
          if (editor.isActive && editor.isActive('link')) {
            const attrs = editor.getAttributes('link');
            currentHref = attrs?.href || '';
            currentTarget = attrs?.target || '';
          }
        } catch (e) {}

        const hasExistingHref = !!(currentHref && currentHref.length);
        setLinkMenu({
          open: true,
          url: currentHref || '',
          target: currentTarget || '',
          x: x || 0,
          y: y || 0,
          editing: !hasExistingHref,
          defaultMode: hasExistingHref ? null : 'project'
        });
      } catch (err) {}
    }), isActive: e => e?.isActive('link') },
    { type: 'tableCreate' },

    { type: 'divider' },

    // Indent / Outdent (onClick handled at render-time via `handleIndent`)
    { label: 'Indent', iconClass: 'text-indent', onClick: () => {}, isActive: () => false },
    { label: 'Outdent', iconClass: 'text-outdent', onClick: () => {}, isActive: () => false },

    { type: 'divider' },

    // Blockquote / Code
    { label: 'Quote', iconClass: 'quotes', onClick: () => runSafe(() => editor?.chain().focus().toggleBlockquote().run()), isActive: e => e?.isActive('blockquote') },
    { label: 'Inline Code', iconClass: 'code', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.toggleCode().run())), isActive: e => e?.isActive('code') },
    { label: 'Code Block', iconClass: 'code-block', onClick: () => runSafe(() => editor?.chain().focus().toggleCodeBlock().run()), isActive: e => e?.isActive('codeBlock') },

    { type: 'divider' },

    // Alignment (using classes)
    { label: 'Align Left', iconClass: 'text-align-left', onClick: () => runSafe(() => updateClasses(editor, 'alL')), isActive: () => hasClass(editor, 'alL') },
    { label: 'Align Center', iconClass: 'text-align-center', onClick: () => runSafe(() => updateClasses(editor, 'alC')), isActive: () => hasClass(editor, 'alC') },
    { label: 'Align Right', iconClass: 'text-align-right', onClick: () => runSafe(() => updateClasses(editor, 'alR')), isActive: () => hasClass(editor, 'alR') },
    { label: 'Justify', iconClass: 'text-align-justify', onClick: () => runSafe(() => updateClasses(editor, 'alJ')), isActive: () => hasClass(editor, 'alJ') },

    { type: 'divider' },

    // Clear formatting
    { label: 'Clear Formatting', iconClass: 'eraser', onClick: () => runSafe(() => runSuggestTrackedInlineCommand((chain) => chain.unsetAllMarks().clearNodes().run())), isActive: () => false },

    { type: 'divider' },

    // View mode buttons
    { label: 'Rich Text', iconClass: 'text-aa', onClick: () => setViewMode('rich'), isActive: () => viewMode === 'rich' },
    { label: 'HTML', iconClass: 'code', onClick: () => { setViewMode('html'); runSafe(() => showHTML(editor, setContent)); }, isActive: () => viewMode === 'html' }
  ];

  // Indent/outdent handler extracted
  const handleIndent = (direction) => {
    // direction: 'in' or 'out'
    if (!editor) return;
    runSafe(() => {
      const inTaskList = editor?.isActive('taskList') || editor?.isActive('taskItem');

      if (direction === 'in') {
        if (inTaskList) {
          editor?.chain().focus().sinkListItem('taskItem').run();
        } else if (editor?.isActive('bulletList') || editor?.isActive('orderedList')) {
          editor?.chain().focus().sinkListItem('listItem').run();
        } else {
          const current = getCurrentIndent(editor);
          if (current < 6) setIndent(editor, current + 1);
        }
      } else {
        if (inTaskList) {
          editor?.chain().focus().liftListItem('taskItem').run();
        } else if (editor?.isActive('bulletList') || editor?.isActive('orderedList')) {
          editor?.chain().focus().liftListItem('listItem').run();
        } else {
          const current = getCurrentIndent(editor);
          if (current > 0) setIndent(editor, current - 1);
        }
      }
    });
  };

  // Build two clean lists: expanded (full toolbar) and condensed (simpler sequence)
  const find = (label) => toolbarItems.find(it => it.label === label);

  const expandedList = toolbarItems; // render directly

  const condensedList = [
    find('Undo'), find('Redo'), { type: 'divider' },
    { type: 'caret', key: 'Edit Mode', title: 'Edit Mode', items: ['Write', 'Suggest'].map(l => find(l)) },
    { type: 'divider' },
    { type: 'caret', key: 'Inline Styles', items: ['Bold','Italic','Underline','Strike'].map(l => find(l)) },
    { type: 'divider' },
    { type: 'caret', key: 'Headers', items: ['H1','H2','H3','H4','H5','H6'].map(l => find(l)) },
    { type: 'divider' },
    { type: 'caret', key: 'Lists', items: ['Bullet List','Ordered List','Task List'].map(l => find(l)) },
    { type: 'divider' },
    find('Link'),
    { type: 'tableCreate' },
    { type: 'divider' },
    find('Indent'), find('Outdent'),
    { type: 'divider' },
    { type: 'caret', key: 'Alignment', items: ['Align Left','Align Center','Align Right','Justify'].map(l => find(l)) },
    { type: 'divider' },
    { type: 'caret', key: 'Block Styles', items: ['Quote','Inline Code','Code Block'].map(l => find(l)) },
    { type: 'divider' },
    find('Clear Formatting'),
    { type: 'divider' }
  ];

  // Render either expandedList or condensedList
  const nodes = (moreActive ? expandedList : condensedList).map((item, index) => {
    if (!item) return null;
    if (item.type === 'divider') return <div key={`d-${index}`} className="toolbar-divider" />;
    if (item.type === 'tableCreate') {
      return (
          <div key={`table-create-${index}`} className="caretWrapper" style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            ref={tableMenuTriggerRef}
            className={`caretButton ${tableMenuOpen ? 'active' : ''}`}
            onMouseDown={e => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              setTableMenuOpen(open => !open);
            }}
            aria-label="Create Table"
            data-tooltip="Create Table"
            data-tooltip-placement="top"
          >
            <Icon icon={'grid-four'} className="icon grid-four" />
            <Icon icon={tableMenuOpen ? 'caret-up-fill' : 'caret-down-fill'} className="icon caretIcon small" />
          </button>
          <TableCreatePicker
            editor={editor}
            isOpen={tableMenuOpen}
            onClose={() => setTableMenuOpen(false)}
            triggerRef={tableMenuTriggerRef}
          />
        </div>
      );
    }
    if (item.type === 'caret') {
      const menuItems = item.items.filter(Boolean);
      return <CaretButton key={item.key || index} items={menuItems} editor={editor} title={item.title || item.key} placement="top" />;
    }
    // normal item (from toolbarItems)
    return (
      <button
        key={item.label || index}
        className={`iconButton ${typeof item.isActive === 'function' && item.isActive(editor) ? 'active' : ''}`}
        onMouseDown={e => e.preventDefault()}
        onClick={item.label === 'Indent' ? () => handleIndent('in') : item.label === 'Outdent' ? () => handleIndent('out') : item.onClick}
        aria-label={item.label}
        data-tooltip={item.tooltip || item.label}
        data-tooltip-placement="top"
      >
        <Icon icon={item.iconClass} className={`icon ${String(item.iconClass || '').replace(/^ph[- ]?/, '').trim()}`} />
      </button>
    );
  }).filter(Boolean);

  // More button (toggle)
  nodes.push(
    <button
      key="more-button"
      className={`iconButton ${moreActive ? 'active' : ''}`}
      onMouseDown={e => e.preventDefault()}
      onClick={() => {
        setMoreActive(a => {
          const next = !a;
          try { setPreference(null, 'toolbarExpanded', !!next); } catch (err) {}
          return next;
        });
      }}
      aria-label={moreActive ? 'Collapse' : 'Expand'}
      data-tooltip={moreActive ? 'Collapse' : 'Expand'}
      data-tooltip-placement="top"
    >
      <Icon icon={'dots-three'} className="icon dots-three" />
    </button>
  );

  // Build className based on current state
  return (
    <>
      <div 
        id={id} 
        onMouseDown={e => e.preventDefault()} 
        onTouchStart={e => e.preventDefault()} 
        tabIndex={-1}
      >
        <div className="toolbar-inner" role="toolbar" aria-label="Editor toolbar">
          {nodes}
        </div>
      </div>
      {linkMenu.open && (
        <PopoverLinks
          editor={editor}
          url={linkMenu.url}
          linkTarget={linkMenu.target}
          defaultMode={linkMenu.defaultMode}
          position={{ x: linkMenu.x, y: linkMenu.y }}
          editing={!!linkMenu.editing}
          projectId={projectId}
          itemsMap={itemsMap}
          onOpenProjectDoc={onOpenProjectDoc}
          onClose={() => setLinkMenu({ open: false, url: '', target: '', x: 0, y: 0, editing: false, defaultMode: null })}
        />
      )}
    </>
  );
}
